import React, { useState } from 'react';
import { Mail, Building2, Globe, User, MapPin, Save, Edit, X, Crown } from 'lucide-react';
import { adminUpdate } from '../../lib/adminApi';

interface UserBasicInfoProps {
  user: {
    id: string;
    email: string;
    first_name: string;
    company_name: string;
    website_url: string | null;
    user_profile: string;
    image_preferences: string;
    address: string | null;
    city: string | null;
    postal_code: string | null;
    country: string | null;
    sendgrid_from_email: string | null;
    sendgrid_from_name: string | null;
    is_pro: boolean;
    created_at: string;
    updated_at: string;
  };
  onUpdate: () => void;
}

export function UserBasicInfo({ user, onUpdate }: UserBasicInfoProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState({
    email: user.email || '',
    first_name: user.first_name || '',
    company_name: user.company_name || '',
    website_url: user.website_url || '',
    user_profile: user.user_profile || '',
    image_preferences: user.image_preferences || '',
    address: user.address || '',
    city: user.city || '',
    postal_code: user.postal_code || '',
    country: user.country || 'Nederland',
    sendgrid_from_email: user.sendgrid_from_email || '',
    sendgrid_from_name: user.sendgrid_from_name || '',
    is_pro: user.is_pro || false,
  });

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');

    try {
      await adminUpdate({
        table: 'users',
        operation: 'update',
        id: user.id,
        data: formData,
      });

      setSuccessMessage('Gegevens succesvol bijgewerkt');
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      onUpdate();
    } catch (error: any) {
      alert('Fout bij opslaan: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      email: user.email || '',
      first_name: user.first_name || '',
      company_name: user.company_name || '',
      website_url: user.website_url || '',
      user_profile: user.user_profile || '',
      image_preferences: user.image_preferences || '',
      address: user.address || '',
      city: user.city || '',
      postal_code: user.postal_code || '',
      country: user.country || 'Nederland',
      sendgrid_from_email: user.sendgrid_from_email || '',
      sendgrid_from_name: user.sendgrid_from_name || '',
      is_pro: user.is_pro || false,
    });
    setIsEditing(false);
    setSuccessMessage('');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Gebruikersgegevens</h2>
        <div className="flex items-center gap-2">
          {successMessage && (
            <span className="text-sm text-green-600 font-medium">{successMessage}</span>
          )}
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              <Edit className="w-4 h-4" />
              Bewerken
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                <X className="w-4 h-4" />
                Annuleren
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              Naam
            </div>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          ) : (
            <p className="text-gray-900">{user.first_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              Email
            </div>
          </label>
          {isEditing ? (
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          ) : (
            <p className="text-gray-900">{user.email}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              Bedrijfsnaam
            </div>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          ) : (
            <p className="text-gray-900">{user.company_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-gray-400" />
              Website
            </div>
          </label>
          {isEditing ? (
            <input
              type="url"
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          ) : (
            <p className="text-gray-900">{user.website_url || '-'}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Gebruikersprofiel</label>
          {isEditing ? (
            <textarea
              value={formData.user_profile}
              onChange={(e) => setFormData({ ...formData, user_profile: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          ) : (
            <p className="text-gray-900">{user.user_profile}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Afbeelding Voorkeuren</label>
          {isEditing ? (
            <textarea
              value={formData.image_preferences}
              onChange={(e) => setFormData({ ...formData, image_preferences: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          ) : (
            <p className="text-gray-900">{user.image_preferences}</p>
          )}
        </div>

        <div className="md:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            Adresgegevens
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Adres</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              ) : (
                <p className="text-gray-900">{user.address || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Stad</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              ) : (
                <p className="text-gray-900">{user.city || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Postcode</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              ) : (
                <p className="text-gray-900">{user.postal_code || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Land</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              ) : (
                <p className="text-gray-900">{user.country || '-'}</p>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-2 border-t border-gray-200 pt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">SendGrid Instellingen</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Van Email</label>
              {isEditing ? (
                <input
                  type="email"
                  value={formData.sendgrid_from_email}
                  onChange={(e) => setFormData({ ...formData, sendgrid_from_email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              ) : (
                <p className="text-gray-900">{user.sendgrid_from_email || '-'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Van Naam</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.sendgrid_from_name}
                  onChange={(e) => setFormData({ ...formData, sendgrid_from_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
              ) : (
                <p className="text-gray-900">{user.sendgrid_from_name || '-'}</p>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-2 border-t border-gray-200 pt-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Aangemaakt:</span>
              <span className="ml-2 text-gray-900">{new Date(user.created_at).toLocaleString('nl-NL')}</span>
            </div>
            <div>
              <span className="text-gray-600">Laatst bijgewerkt:</span>
              <span className="ml-2 text-gray-900">{new Date(user.updated_at).toLocaleString('nl-NL')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
